import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

export interface PageSeo {
  title: string;
  description: string;
  canonical: string;
  noindex?: boolean;
  image?: string;
}

const SITE_NAME = 'Kanyar Büfé';
const DEFAULT_IMAGE = 'https://kanyarbufe.hu/og-image.jpg';

/**
 * Per-route SEO frissítések: title, meta description, robots, canonical,
 * Open Graph és Twitter Card. Az `index.html` statikus alapértékeit írja felül
 * a kliens-oldali navigáció után.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly titleService = inject(Title);
  private readonly metaService = inject(Meta);
  private readonly document = inject(DOCUMENT);

  setPageSeo(seo: PageSeo): void {
    this.titleService.setTitle(seo.title);

    this.upsertMeta({ name: 'description' }, seo.description);

    const robots = seo.noindex
      ? 'noindex, follow'
      : 'index, follow, max-image-preview:large, max-snippet:-1';
    this.upsertMeta({ name: 'robots' }, robots);
    this.upsertMeta({ name: 'googlebot' }, robots);
    this.upsertMeta({ name: 'bingbot' }, robots);

    this.upsertCanonical(seo.canonical);

    const image = seo.image ?? DEFAULT_IMAGE;
    this.upsertMeta({ property: 'og:title' }, seo.title);
    this.upsertMeta({ property: 'og:description' }, seo.description);
    this.upsertMeta({ property: 'og:url' }, seo.canonical);
    this.upsertMeta({ property: 'og:image' }, image);
    this.upsertMeta({ property: 'og:site_name' }, SITE_NAME);
    this.upsertMeta({ property: 'og:type' }, 'restaurant');
    this.upsertMeta({ property: 'og:locale' }, 'hu_HU');

    this.upsertMeta({ name: 'twitter:card' }, 'summary_large_image');
    this.upsertMeta({ name: 'twitter:title' }, seo.title);
    this.upsertMeta({ name: 'twitter:description' }, seo.description);
    this.upsertMeta({ name: 'twitter:image' }, image);
  }

  private upsertMeta(
    selector: { name?: string; property?: string },
    content: string
  ): void {
    const sel = selector.name
      ? `name="${selector.name}"`
      : `property="${selector.property}"`;
    if (this.metaService.getTag(sel)) {
      this.metaService.updateTag({ ...selector, content }, sel);
    } else {
      this.metaService.addTag({ ...selector, content });
    }
  }

  private upsertCanonical(href: string): void {
    const head = this.document.head;
    let link = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', href);
  }
}
